import { CsvImportProps } from '../../views/CreateOperator/CsvImportButton';
import { Input } from '../Input';
import { Button } from '../Button/NewButton';
import { GetApp } from '@material-ui/icons';
import { Divider } from '../Divider';
import { ChangeEvent, useEffect, useState } from 'react';
import {
  CsvImportColumn,
  CsvImportResult,
  downloadCsv,
  readCsv,
} from '../../lib/csv';
import { randomNumber } from '../../lib/constants';
import { Checkbox } from '../Checkbox';

interface Props<T> extends CsvImportProps<T> {
  open: boolean;
  onClose?(): void;
}
export function CSVImport<T>({
  canOverwrite,
  hideOverwriteMessage = false,
  requiredColumns,
  onImport,
  open,
  onClose = () => null,
  settingName,
}: Props<T>) {
  const [csv, setCsv] = useState<CsvImportResult<T> | null>(null);
  const [overwrite, setOverwrite] = useState(false);

  useEffect(() => {
    if (open === false) {
      setCsv(null);
    }
  }, [open]);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    setCsv(null);
    event.preventDefault();
    event.stopPropagation();
    if (event.target.files && event.target.files.length > 0) {
      readCsv<T>(event.target.files[0], handleCsvCallback, requiredColumns);
    }
  };

  const handleCsvCallback = (csv: CsvImportResult<T>) => setCsv(csv);

  const renderCsvResults = () => {
    if (csv === null || csv?.data.length === 0) return;
    return (
      <p className="text-success">
        {(csv.data ?? []).length} {settingName} found
      </p>
    );
  };

  const handleDownload = () => {
    const template = requiredColumns.reduce(
      (obj: { columns: any[]; data: object }, col) => {
        obj.columns.push({
          name: col.name,
          dataKey: col.name,
        });

        switch (col.type) {
          case 'boolean':
            obj.data = Object.assign(obj.data, { [col.name]: true });
            break;
          case 'number':
            obj.data = Object.assign(obj.data, { [col.name]: randomNumber() });
            break;
          case 'string':
          default:
            obj.data = Object.assign(obj.data, {
              [col.name]:
                col.options !== undefined ? col.options.join('|') : 'N/A',
            });
            break;
        }
        return obj;
      },
      { columns: [], data: {} }
    );
    downloadCsv(
      template.columns,
      [template.data],
      `${settingName}_template`,
      true
    );
  };

  const renderNote = (column: CsvImportColumn) => {
    if (column.note === undefined) return;
    return <p className="text-xs opacity-70">Note: {column.note}</p>;
  };

  const renderOptions = (column: CsvImportColumn) => {
    if (column.options === undefined) return;
    return (
      <span className="text-secondary text-xs">
        {column.options.map((option, index) => {
          if (index + 1 === column.options?.length) return `or ${option}`;
          else if (index + 1 === (column.options ?? []).length - 1)
            return `${option} `;
          else return `${option}, `;
        })}
      </span>
    );
  };

  const renderCheckbox = () => {
    if (canOverwrite)
      return (
        <Checkbox
          label=""
          onChange={(event) => setOverwrite(event.target.checked)}
          checked={overwrite}
        />
      );
    return;
  };

  const renderOverwriteMessage = () => {
    let header = `Replace Current ${settingName}`;
    let message = `Checking this box will overwrite the current ${settingName} you have already added`;

    if (canOverwrite === false) {
      header = `Settings Overwrite`;
      message = `All ${settingName} will be overwritten on import. If more than 1 row is provided, only the first one will be used.`;
    }

    return (
      <>
        <p>{header}</p>
        <p className="text-xs opacity-70">{message}</p>
      </>
    );
  };

  return (
    <>
      {renderCsvResults()}
      <Input
        className="w-2/5"
        accept=".csv"
        error={Boolean(csv?.error)}
        helperText={csv && csv.error ? csv.error : undefined}
        variant="contained"
        type="file"
        onChange={handleChange}
      />
      <div className="flex flex-col items-start py-4">
        <div className="flex items-center justify-between gap-2 w-full">
          <h4 className="text-lg">Import Requirements</h4>
          <Button
            theme="text"
            color="inherit"
            startIcon={<GetApp fontSize="small" />}
            typeCasing="capitalize"
            onClick={() => handleDownload()}
          >
            Template
          </Button>
        </div>
        <ul className="ml-4 list-disc p-2">
          <li>
            Values with commas must be enclosed with quotes ("Sample, value")
          </li>
          <li>CSV must contain the following columns</li>
          <div className="max-h-[200px] overflow-auto w-full">
            <ol className="list-decimal ml-6 p-2">
              {requiredColumns.map((column, index) => (
                <li key={`required-column-${index + 1}`}>
                  {column.name}{' '}
                  <span className="text-primary italic">{column.type} </span>{' '}
                  {renderOptions(column)}{' '}
                  <span className="text-xs opacity-70">
                    {column.nullable && '(optional)'}
                  </span>
                  {renderNote(column)}
                </li>
              ))}
            </ol>
          </div>
        </ul>
      </div>
      <div className="flex flex-col gap-4 items-center justify-center w-2/5">
        {!hideOverwriteMessage ? (
          <>
            <Divider />
            <div className="inline-flex items-center p-2">
              <div>{renderOverwriteMessage()}</div>
              {renderCheckbox()}
            </div>
            <Divider />
          </>
        ) : null}

        <Button
          theme="action"
          disabled={csv === null || csv.error}
          onClick={() => {
            if (csv) {
              onImport(csv.data ?? [], overwrite);
              onClose();
            }
          }}
          fullWidth
        >
          Import CSV
        </Button>
      </div>
    </>
  );
}
